import { apiClient } from '../lib/apiClient';
import type {
  LivestockIdentificationConfidence,
  LivestockIdentificationTaxonomy,
} from './livestockIdentificationService';

/**
 * "Analyse an overview photo" (backend: src/controllers/livestockOverview.controller.ts):
 * one photo of the whole tank → the AI finds the animals (call 1), every animal is cut
 * out of the full-resolution photo and all cut-outs are identified in one batched request
 * (call 2). The analysis runs in the background; poll it until `ready` or `failed`.
 */

export type LivestockOverviewStatus = 'detecting' | 'identifying' | 'ready' | 'failed';
export type OverviewCategory = 'coral' | 'fish' | 'invertebrate' | 'other';

/** Why an analysis failed or is incomplete. */
export type OverviewErrorCode =
  | 'AI_BUDGET_EXCEEDED'
  | 'AI_TEMPORARILY_UNAVAILABLE'
  | 'TIMEOUT'
  | 'PROVIDER_ERROR'
  | 'PROCESSING_ERROR';

export interface OverviewSuggestion {
  category: string;
  taxonomy: LivestockIdentificationTaxonomy;
  commonNames: string[];
  confidence: LivestockIdentificationConfidence;
  featureExplanation: string;
}

/** Bounding box in normalised image coordinates (0–1, origin top left). */
export interface OverviewBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface OverviewCandidate {
  id: string;
  /** 1-based number shown in the list and on the photo. */
  position: number;
  box: OverviewBox;
  detectedLabel: string | null;
  detectedCategory: OverviewCategory | null;
  detectionConfidence: number | null;
  /** Best first; null when the animal could not be identified. */
  suggestions: OverviewSuggestion[] | null;
  commonName: string;
  scientificName: string | null;
  category: OverviewCategory;
  selected: boolean;
  created: boolean;
  livestockId: string | null;
  /** Signed URL of the cut-out (null when unavailable). */
  imageUrl: string | null;
}

export interface OverviewAnalysis {
  id: string;
  aquariumId: string;
  status: LivestockOverviewStatus;
  errorCode: OverviewErrorCode | string | null;
  /** Signed URL of the photo as the AI saw it (for the numbered markers). */
  imageUrl: string | null;
  imageWidth: number | null;
  imageHeight: number | null;
  creditsUsed: number;
  createdAt: string;
  expiresAt: string;
  candidates: OverviewCandidate[];
}

export interface OverviewAnalysisSummary {
  id: string;
  status: LivestockOverviewStatus;
  errorCode: string | null;
  imageUrl: string | null;
  candidateCount: number;
  createdCount: number;
  createdAt: string;
  expiresAt: string;
}

export interface OverviewCandidatePatch {
  selected?: boolean;
  commonName?: string;
  scientificName?: string | null;
  category?: OverviewCategory;
}

export interface OverviewCreateItem {
  candidateId: string;
  commonName: string;
  scientificName?: string | null;
  category: OverviewCategory;
}

export interface OverviewCreateResult {
  created: Array<{ candidateId: string; livestockId: string; photoAttached: boolean }>;
  skipped: string[];
  photoAttachFailed: number;
}

/** Largest accepted overview photo, in MB (the backend allows 20 MB). */
export const MAX_OVERVIEW_PHOTO_MB = 20;

const base = (aquariumId: string): string =>
  `/api/v1/aquariums/${encodeURIComponent(aquariumId)}/livestock/overview-analyses`;

/** Uploads the photo and starts the analysis (202: status `detecting`). */
export async function startOverviewAnalysis(aquariumId: string, file: File, language?: string): Promise<OverviewAnalysis> {
  const formData = new FormData();
  if (language) formData.append('language', language);
  formData.append('photo', file);
  const response = await apiClient.post<{ data: OverviewAnalysis }>(base(aquariumId), formData);
  return response.data;
}

export async function getOverviewAnalysis(aquariumId: string, analysisId: string): Promise<OverviewAnalysis> {
  const response = await apiClient.get<{ data: OverviewAnalysis }>(`${base(aquariumId)}/${encodeURIComponent(analysisId)}`);
  return response.data;
}

export async function listOverviewAnalyses(aquariumId: string): Promise<OverviewAnalysisSummary[]> {
  const response = await apiClient.get<{ data: OverviewAnalysisSummary[] }>(base(aquariumId));
  return Array.isArray(response.data) ? response.data : [];
}

export async function updateOverviewCandidate(
  aquariumId: string,
  analysisId: string,
  candidateId: string,
  patch: OverviewCandidatePatch,
): Promise<OverviewCandidate> {
  const response = await apiClient.patch<{ data: OverviewCandidate }>(
    `${base(aquariumId)}/${encodeURIComponent(analysisId)}/candidates/${encodeURIComponent(candidateId)}`,
    patch,
  );
  return response.data;
}

/** Creates the listed candidates as livestock (only those, each once). */
export async function createOverviewLivestock(
  aquariumId: string,
  analysisId: string,
  items: OverviewCreateItem[],
  dateAdded?: string,
): Promise<OverviewCreateResult> {
  const response = await apiClient.post<{ data: OverviewCreateResult }>(
    `${base(aquariumId)}/${encodeURIComponent(analysisId)}/livestock`,
    { items, ...(dateAdded ? { dateAdded } : {}) },
  );
  return response.data;
}

export async function deleteOverviewAnalysis(aquariumId: string, analysisId: string): Promise<void> {
  await apiClient.delete<unknown>(`${base(aquariumId)}/${encodeURIComponent(analysisId)}`);
}

/** True while the background run is still working. */
export function isProcessing(status: LivestockOverviewStatus | undefined): boolean {
  return status === 'detecting' || status === 'identifying';
}
